import React, { useState, useEffect, useMemo } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { eventsApi } from '@/lib/api';
import { Calendar, Clock, MapPin, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => { loadEvents(); }, []);

  const loadEvents = async () => {
    try {
      const data = await eventsApi.getAll(false);
      setEvents(data);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const recurringEvents = events.filter(e => e.is_recurring);
  const allEvents = events;

  // Generate recurring event occurrences for a given month
  const getEventsForMonth = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const eventsByDate = {};

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay();
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      eventsByDate[dateKey] = [];

      allEvents.forEach(event => {
        const eventDate = new Date(event.date);

        if (event.is_recurring) {
          const freq = event.recurrence_frequency;
          if (freq === 'daily') {
            eventsByDate[dateKey].push(event);
          } else if (freq === 'weekly' && event.recurrence_day !== null && event.recurrence_day !== undefined) {
            if (dayOfWeek === event.recurrence_day) {
              eventsByDate[dateKey].push(event);
            }
          } else if (freq === 'biweekly' && event.recurrence_day !== null && event.recurrence_day !== undefined) {
            if (dayOfWeek === event.recurrence_day) {
              const startDate = new Date(event.date);
              const diffWeeks = Math.floor((date - startDate) / (7 * 24 * 60 * 60 * 1000));
              if (diffWeeks >= 0 && diffWeeks % 2 === 0) {
                eventsByDate[dateKey].push(event);
              }
            }
          } else if (freq === 'monthly' && event.recurrence_date) {
            if (day === event.recurrence_date) {
              eventsByDate[dateKey].push(event);
            }
          } else if (freq === 'yearly') {
            if (eventDate.getMonth() === month && eventDate.getDate() === day) {
              eventsByDate[dateKey].push(event);
            }
          }
        } else {
          // One-time event
          if (eventDate.getFullYear() === year && eventDate.getMonth() === month && eventDate.getDate() === day) {
            eventsByDate[dateKey].push(event);
          }
        }
      });
    }
    return eventsByDate;
  }, [allEvents, calendarMonth]);

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);

    return days;
  }, [calendarMonth]);

  const selectedDateEvents = useMemo(() => {
    if (!selectedDate) return [];
    const key = `${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}-${String(selectedDate).padStart(2, '0')}`;
    return getEventsForMonth[key] || [];
  }, [selectedDate, getEventsForMonth, calendarMonth]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const groupEventsByMonth = () => {
    const grouped = {};
    // Only show non-recurring events in the timeline, or all events
    allEvents.forEach(event => {
      const date = new Date(event.date);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!grouped[key]) {
        grouped[key] = { label, events: [] };
      }
      grouped[key].events.push(event);
    });
    return Object.values(grouped);
  };

  const groupedEvents = groupEventsByMonth();

  const prevMonth = () => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  const nextMonth = () => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));

  const today = new Date();
  const isToday = (day) => day && today.getFullYear() === calendarMonth.getFullYear() && today.getMonth() === calendarMonth.getMonth() && today.getDate() === day;

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-r from-stone-800 to-stone-700 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            Events & Calendar
          </h1>
          <p className="text-xl text-stone-300 max-w-2xl mx-auto">
            Join us for worship, fellowship, and community events
          </p>
        </div>
      </section>

      {/* Regular Church Activities */}
      {recurringEvents.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-serif font-bold text-stone-800 mb-8 text-center" data-testid="regular-activities-heading">
              Regular Church Activities
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {recurringEvents.map((activity) => (
                <Card key={activity.id} className="hover:shadow-md transition-shadow" data-testid={`regular-activity-${activity.id}`}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-stone-800 mb-2">{activity.title}</h3>
                    <p className="text-amber-700 font-medium text-sm flex items-center gap-1">
                      <RefreshCw className="h-3.5 w-3.5" />
                      {activity.recurrence_pattern || 'Recurring'}
                    </p>
                    {activity.description && (
                      <p className="text-sm text-stone-500 mt-2">{activity.description}</p>
                    )}
                    {activity.location && (
                      <p className="text-sm text-stone-400 mt-1 flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" /> {activity.location}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Calendar View */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-serif font-bold text-stone-800 mb-8 text-center">Calendar</h2>

          <Card>
            <CardContent className="p-4 md:p-6">
              {/* Calendar Navigation */}
              <div className="flex items-center justify-between mb-6">
                <Button variant="outline" size="sm" onClick={prevMonth} data-testid="cal-prev-month">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h3 className="text-lg font-semibold text-stone-800" data-testid="cal-month-label">
                  {MONTHS[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                </h3>
                <Button variant="outline" size="sm" onClick={nextMonth} data-testid="cal-next-month">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-px bg-stone-200 rounded-lg overflow-hidden">
                {/* Day headers */}
                {DAYS_OF_WEEK.map(day => (
                  <div key={day} className="bg-stone-100 text-center py-2 text-xs font-semibold text-stone-600 uppercase">
                    {day}
                  </div>
                ))}

                {/* Day cells */}
                {calendarDays.map((day, i) => {
                  if (!day) return <div key={`empty-${i}`} className="bg-white min-h-[60px] md:min-h-[80px]" />;

                  const dateKey = `${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayEvents = getEventsForMonth[dateKey] || [];
                  const hasEvents = dayEvents.length > 0;
                  const isSelected = selectedDate === day;

                  return (
                    <div
                      key={day}
                      onClick={() => setSelectedDate(isSelected ? null : day)}
                      className={`bg-white min-h-[60px] md:min-h-[80px] p-1 cursor-pointer transition-colors hover:bg-amber-50 ${isSelected ? 'ring-2 ring-amber-600 ring-inset' : ''}`}
                      data-testid={`cal-day-${day}`}
                    >
                      <div className={`text-sm font-medium mb-1 ${isToday(day) ? 'bg-amber-700 text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-stone-700'}`}>
                        {day}
                      </div>
                      {hasEvents && (
                        <div className="space-y-0.5">
                          {dayEvents.slice(0, 2).map((ev, j) => (
                            <div key={j} className={`text-[10px] md:text-xs truncate px-1 rounded ${ev.is_recurring ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                              {ev.title}
                            </div>
                          ))}
                          {dayEvents.length > 2 && (
                            <div className="text-[10px] text-stone-500 px-1">+{dayEvents.length - 2} more</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Selected Date Events */}
              {selectedDate && (
                <div className="mt-6 border-t pt-4" data-testid="selected-date-events">
                  <h4 className="font-semibold text-stone-800 mb-3">
                    {MONTHS[calendarMonth.getMonth()]} {selectedDate}, {calendarMonth.getFullYear()}
                  </h4>
                  {selectedDateEvents.length > 0 ? (
                    <div className="space-y-3">
                      {selectedDateEvents.map((ev, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-stone-50">
                          <div className={`w-1 h-full min-h-[40px] rounded-full shrink-0 ${ev.is_recurring ? 'bg-amber-600' : 'bg-blue-600'}`} />
                          <div>
                            <div className="font-medium text-stone-800">{ev.title}</div>
                            {ev.description && <p className="text-sm text-stone-500">{ev.description}</p>}
                            <div className="flex flex-wrap gap-3 text-xs text-stone-400 mt-1">
                              {ev.event_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{ev.event_time}</span>}
                              {!ev.event_time && ev.date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatTime(ev.date)}</span>}
                              {ev.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{ev.location}</span>}
                              {ev.is_recurring && <span className="flex items-center gap-1 text-amber-600"><RefreshCw className="h-3 w-3" />{ev.recurrence_pattern}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-sm">No events on this date.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Events List */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-serif font-bold text-stone-800 mb-8 text-center">Upcoming Events</h2>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700 mx-auto"></div>
              <p className="mt-4 text-stone-600">Loading events...</p>
            </div>
          ) : groupedEvents.length > 0 ? (
            <div className="space-y-12">
              {groupedEvents.map((group) => (
                <div key={group.label}>
                  <h3 className="text-xl font-serif font-bold text-stone-800 mb-6 pb-2 border-b-2 border-amber-700">
                    {group.label}
                  </h3>
                  <div className="space-y-4">
                    {group.events.map((event) => (
                      <Card key={event.id} className="hover:shadow-lg transition-shadow" data-testid="event-card">
                        <CardContent className="p-6">
                          <div className="md:flex md:items-start md:gap-6">
                            <div className="hidden md:block text-center min-w-[80px] py-2 px-4 bg-amber-100 rounded-lg">
                              <div className="text-3xl font-bold text-amber-700">
                                {new Date(event.date).getDate()}
                              </div>
                              <div className="text-sm text-amber-600 uppercase">
                                {new Date(event.date).toLocaleDateString('en-US', { weekday: 'short' })}
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-semibold text-stone-800 mb-2">{event.title}</h4>
                              {event.description && <p className="text-stone-600 mb-3">{event.description}</p>}
                              <div className="flex flex-wrap gap-4 text-sm text-stone-500">
                                <span className="flex items-center">
                                  <Calendar className="h-4 w-4 mr-1 text-amber-700" />
                                  {formatDate(event.date)}
                                </span>
                                <span className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1 text-amber-700" />
                                  {event.event_time || formatTime(event.date)}
                                  {event.end_date && ` - ${formatTime(event.end_date)}`}
                                </span>
                                <span className="flex items-center">
                                  <MapPin className="h-4 w-4 mr-1 text-amber-700" />
                                  {event.location}
                                </span>
                              </div>
                              {event.is_recurring && event.recurrence_pattern && (
                                <p className="mt-2 text-sm text-amber-700 font-medium flex items-center gap-1">
                                  <RefreshCw className="h-3.5 w-3.5" /> {event.recurrence_pattern}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-8 text-center">
                <Calendar className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-stone-800 mb-2">No Events Scheduled</h3>
                <p className="text-stone-600">Check back soon for upcoming church events and activities.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default EventsPage;
